import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { api, apiErrorMessage } from '../api/client';
import { useAuth, StaffProfile } from '../auth/AuthContext';

type Step = 'email' | 'otp' | 'mfa';

interface Enrollment {
  secret: string;
  otpauth_uri: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [totp, setTotp] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/admin/auth/otp/request', { email });
      setStep('otp');
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to send verification code.'));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post('/api/admin/auth/otp/verify', { email, code: otp });
      if (res.data.access_token) {
        login(res.data.access_token, res.data.staff as StaffProfile);
        navigate('/', { replace: true });
        return;
      }
      setMfaToken(res.data.mfa_token);
      if (res.data.mfa_enrollment) setEnrollment(res.data.mfa_enrollment);
      setStep('mfa');
    } catch (err) {
      setError(apiErrorMessage(err, 'Invalid or expired code.'));
    } finally {
      setBusy(false);
    }
  };

  const verifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post('/api/admin/auth/mfa/verify', {
        mfa_token: mfaToken,
        totp_code: totp,
      });
      login(res.data.access_token, res.data.staff as StaffProfile);
      navigate('/', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Invalid authentication code.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold uppercase tracking-wide text-white">
            Florida Department of Health
          </h1>
          <p className="text-sm text-slate-300">
            Immunization Onboarding Admin Portal
          </p>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-xl">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={requestOtp} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Staff Sign In</h2>
                <p className="text-xs text-slate-500">
                  Authorized Florida DOH &amp; school health personnel only.
                </p>
              </div>
              <label className="block text-sm font-medium text-slate-700">
                Official Email Address
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nurse.smith@flhealth.gov"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-md bg-blue-600 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Sending…' : 'Send Verification Code'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={verifyOtp} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  Enter Verification Code
                </h2>
                <p className="text-xs text-slate-500">
                  A 6-digit code was emailed to {email}.
                </p>
              </div>
              <input
                inputMode="numeric"
                required
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                placeholder="123456"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-center text-lg tracking-[0.5em] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-md bg-blue-600 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Verifying…' : 'Continue'}
              </button>
            </form>
          )}

          {step === 'mfa' && (
            <form onSubmit={verifyMfa} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  Two-Factor Authentication
                </h2>
                <p className="text-xs text-slate-500">
                  Enter the 6-digit code from your authenticator app.
                </p>
              </div>

              {enrollment && (
                <div className="rounded-md bg-slate-50 p-3 text-center">
                  <p className="mb-2 text-xs text-slate-600">
                    First time here — scan this QR code with Google Authenticator,
                    Authy, or a YubiKey authenticator, then enter the code below.
                  </p>
                  <div className="flex justify-center">
                    <QRCodeSVG value={enrollment.otpauth_uri} size={148} />
                  </div>
                  <p className="mt-2 break-all font-mono text-[10px] text-slate-400">
                    {enrollment.secret}
                  </p>
                </div>
              )}

              <input
                inputMode="numeric"
                required
                autoFocus
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                placeholder="654321"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-center text-lg tracking-[0.5em] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-md bg-blue-600 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Verifying…' : 'Verify & Enter Portal'}
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-[11px] text-slate-400">
          Access is monitored and logged per SOC 2 standards and Florida
          Statutes § 381.003.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
